import { useContext, useEffect, useMemo, useState } from 'react'
import { WalletAddressContext } from '@/lib/walletContext'
import { isOnChainCurveEnabled } from '@/lib/launchpad/contracts'
import { readWalletEthBalance, readWalletTokenBalance } from '@/lib/launchpad/holders'
import {
  getSimHoldingForLaunch,
  getSimHoldings,
  getSimLaunch,
  getSimWalletWei,
  listSimLaunches,
  subscribeSimLaunchpad,
} from '@/lib/launchpad/store'
import { sameTradedLaunch, subscribeLaunchTraded } from '@/lib/launchpad/tradeEvents'
import type { LaunchState } from '@/lib/launchpad/types'

/**
 * How often to poll live on-chain ETH/token balances.
 * IMPORTANT: this must NOT be driven by the store's `tick` counter.
 * Store ticks fire on every hydration pass (on mount, every 60s, and on
 * every launch/presale mutation), often multiple times within a second.
 * A tick-driven debounce would keep getting cancelled before it could
 * resolve, permanently leaving on-chain balances at 0 even though the
 * wallet actually holds funds/tokens (see Holders panel showing correct
 * balances via the same reader, unaffected by this bug).
 */
const BALANCE_POLL_MS = 15_000

function useSimLaunchStoreTick() {
  const [tick, setTick] = useState(0)
  useEffect(() => subscribeSimLaunchpad(() => setTick(value => value + 1)), [])
  return tick
}

/** Listings-only: no wallet ETH polling. Use on Home / See all. */
export function useSimLaunchList() {
  const tick = useSimLaunchStoreTick()
  const launches = useMemo(() => listSimLaunches(), [tick])
  return { launches, tick }
}

export function useSimLaunchpad() {
  const address = useContext(WalletAddressContext)
  const { launches, tick } = useSimLaunchList()
  const [onChainWei, setOnChainWei] = useState(0n)
  const live = isOnChainCurveEnabled()

  useEffect(() => {
    if (!live || !address) {
      setOnChainWei(0n)
      return
    }

    let active = true

    const refresh = () => {
      readWalletEthBalance(address).then((amount) => {
        if (active)
          setOnChainWei(amount)
      }).catch(() => {})
    }

    refresh()
    const interval = window.setInterval(refresh, BALANCE_POLL_MS)
    const unsubscribe = subscribeLaunchTraded(() => {
      if (!active)
        return
      refresh()
      window.setTimeout(() => {
        if (active)
          refresh()
      }, 1200)
    })

    return () => {
      active = false
      window.clearInterval(interval)
      unsubscribe()
    }
  }, [address, live])

  const localWei = useMemo(() => getSimWalletWei(address), [tick, address])
  const holdings = useMemo(() => getSimHoldings(address), [tick, address])
  const walletWei = live && address ? onChainWei : localWei

  return { launches, walletWei, holdings, address, tick }
}

export function useSimLaunch(id: string | undefined): LaunchState | undefined {
  const { tick } = useSimLaunchList()
  return useMemo(() => (id ? getSimLaunch(id) : undefined), [id, tick])
}

export function useSimHolding(id: string | undefined): bigint {
  const address = useContext(WalletAddressContext)
  const { tick } = useSimLaunchList()
  const launch = useMemo(() => (id ? getSimLaunch(id) : undefined), [id, tick])
  const localHolding = useMemo(
    () => (launch ? getSimHoldingForLaunch(launch, address) : 0n),
    [launch, address, tick],
  )
  const [onChainHolding, setOnChainHolding] = useState(0n)
  const [onChainReady, setOnChainReady] = useState(false)
  const liveHolding = Boolean(isOnChainCurveEnabled() && (launch?.contractAddress || launch?.tokenAddress))
  const launchKey = launch?.contractAddress || launch?.tokenAddress || launch?.id

  // Fetch immediately on (address, id, launchKey) change and then poll on a
  // fixed interval that is independent of the store's `tick`, so rapid
  // hydration updates can never cancel an in-flight balance read forever.
  useEffect(() => {
    if (!id || !address) {
      setOnChainHolding(0n)
      setOnChainReady(false)
      return
    }

    let active = true
    let retrying = false

    const refresh = () => {
      const current = getSimLaunch(id)
      if (!current)
        return
      readWalletTokenBalance(current, address).then((amount) => {
        if (!active)
          return
        retrying = false
        setOnChainHolding(amount)
        setOnChainReady(true)
      }).catch(() => {
        if (!active || retrying)
          return
        retrying = true
        window.setTimeout(() => {
          if (active)
            refresh()
        }, 800)
      })
    }

    refresh()
    const interval = window.setInterval(refresh, BALANCE_POLL_MS)
    const unsubscribe = subscribeLaunchTraded((traded) => {
      if (!active)
        return
      const current = getSimLaunch(id)
      if (!current || !sameTradedLaunch(current, traded))
        return
      refresh()
      window.setTimeout(() => {
        if (active)
          refresh()
      }, 1200)
      window.setTimeout(() => {
        if (active)
          refresh()
      }, 3500)
    })

    return () => {
      active = false
      window.clearInterval(interval)
      unsubscribe()
    }
  }, [address, id, launchKey])

  if (liveHolding && onChainReady)
    return onChainHolding
  return onChainHolding > localHolding ? onChainHolding : localHolding
}
